import Link from "next/link";
import { useRouter } from "next/router";
import { classNames } from "primereact/utils";
import { ReactNode } from "react";
import { UrlObject } from "url";
import { getPathname } from "../../../../../common/utils";
import { SIDEBAR_ACCENT_CLASSNAMES } from "../sidebar.constants";

interface NavItemProps {
    label: string;
    icon: ReactNode;
    href?: string | UrlObject;
    isCompanyView: boolean;
    onClick?: () => void;
    children?: ReactNode;
}

const NavItem = (props: NavItemProps) => {
    const { label, icon, href, isCompanyView, onClick, children } = props;

    const { pathname } = useRouter();

    const isActive = getPathname(href) === pathname;
    const accent = isCompanyView
        ? SIDEBAR_ACCENT_CLASSNAMES.COMPANY
        : SIDEBAR_ACCENT_CLASSNAMES.DEFAULT;

    return (
        <Link
            href={href ?? ""}
            onClick={onClick}
            className={classNames(
                "group relative flex items-center gap-2.5 h-11 md:h-10 rounded-md px-3 py-2 no-underline transition-colors",
                {
                    [accent.activeBg]: isActive,
                    [accent.activeText]: isActive,
                    "text-text-500": !isActive,
                    [accent.hoverBg]: !isActive,
                    [accent.hoverText]: !isActive,
                }
            )}
        >
            {isActive && (
                <span
                    aria-hidden
                    className={classNames(
                        "hidden md:block absolute -left-2.5 top-1/2 -translate-y-1/2 w-0.75 h-5.5 rounded-r-[0.1875rem]",
                        accent.indicator
                    )}
                />
            )}
            <span
                className={classNames(
                    "flex-none",
                    accent.iconHover,
                    isActive ? accent.iconActive : "text-text-300"
                )}
            >
                {icon}
            </span>
            <span
                className={classNames("text-[0.9375rem] md:text-base", {
                    "font-semibold md:font-bold": isActive,
                    "font-normal": !isActive,
                })}
            >
                {label}
            </span>
            {children}
        </Link>
    );
};

export default NavItem;
